import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusCircles from '../StatusCircles';
import type { MilestoneStatus } from '../../types';
import { asksToComplete, progressAfterDrag, progressAfterStatus, progressOnNotYet, snapProgress, statusFloor, stepProgress } from './progressModel';

/**
 * P-10e (D-82) — a thin progress bar dragged in steps of 10%. Quiet by
 * design: a native range (keyboard: arrows move 10%), the brand accent and
 * the percent beside it. No big track, no ticks.
 */
export function ProgressSlider({ value, onChange, disabled = false, label, className = '', floor = 0 }: {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** The accessible name ("התקדמות של …"). */
  label: string;
  className?: string;
  /** QA-3 #3 — the status floor (0/25/50/75/100), shown exactly. */
  floor?: number;
}) {
  const pct = snapProgress(value, floor);
  return (
    <div className={`flex items-center gap-2 ${className}`} data-progress-slider="">
      {/* step 5 so 25 / 75 sit on the track; each move snaps to the next
          step of 10 above the floor (stepProgress). */}
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={pct}
        disabled={disabled}
        onChange={(e) => onChange(stepProgress(pct, Number(e.target.value), floor))}
        aria-label={label}
        aria-valuetext={`${pct}%`}
        className="h-1.5 min-w-24 flex-1 cursor-pointer accent-primary-600 disabled:cursor-default disabled:opacity-60"
      />
      <span className="w-10 shrink-0 text-end text-xs font-medium tabular-nums text-text-2" aria-hidden="true">{pct}%</span>
    </div>
  );
}

export interface TaskProgressValue {
  status: string;
  progress: number;
}

/**
 * P-10e (D-82) — the status circles of a task and, under them, its progress
 * bar, kept in sync:
 *   - a circle moves the bar up to the status's value (unless already higher);
 *   - 🟢 "הושלם" sets 100%;
 *   - dragging to 100% asks "לסמן כהושלמה?" inline (yes → 🟢; not yet →
 *     the bar stays at 100% and the status as it was).
 * The same field serves the ⏸ panel, the ✓ window and the break / clock-out
 * window. Nothing is saved here: the owner saves.
 */
export default function TaskProgressField({ value, onChange, title, disabled = false, size = 'md' }: {
  value: TaskProgressValue;
  onChange: (next: TaskProgressValue) => void;
  /** The task's name (accessible names). */
  title: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
}) {
  const { t } = useTranslation();
  const askId = useId();
  const [asking, setAsking] = useState(false);
  // D-124 — the bar before the drag that reached 100% ("עדיין לא" restores it).
  const [beforeAsk, setBeforeAsk] = useState<number | null>(null);

  const setStatus = (s: MilestoneStatus) => {
    setAsking(false);
    setBeforeAsk(null);
    onChange({ status: s, progress: progressAfterStatus(value.progress, s) });
  };
  const setProgress = (v: number) => {
    const next = progressAfterDrag(v, value.status);
    const ask = asksToComplete(next, value.status);
    if (ask && !asking) setBeforeAsk(value.progress);
    if (!ask) setBeforeAsk(null);
    setAsking(ask);
    onChange({ status: value.status, progress: next });
  };

  return (
    <div className="space-y-1.5" data-task-progress="">
      <StatusCircles
        status={value.status}
        size={size}
        onChange={disabled ? undefined : setStatus}
        label={t('taskPage.statusOf', { name: title })}
      />
      <ProgressSlider
        value={value.progress}
        onChange={setProgress}
        disabled={disabled}
        floor={statusFloor(value.status)}
        label={t('timer.progress.of', { name: title })}
        className="max-w-xs"
      />
      {asking && (
        <div role="group" aria-labelledby={askId} className="flex flex-wrap items-center gap-2 text-sm" data-progress-ask-complete="">
          <span id={askId} className="text-text">{t('timer.progress.askComplete')}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus('completed')}>
            {t('timer.progress.markComplete')}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              // D-124 (QX-58) — "עדיין לא": back to the bar before the drag; 100% is not saved.
              setAsking(false);
              if (beforeAsk !== null) onChange({ status: value.status, progress: progressOnNotYet(beforeAsk, value.status) });
              setBeforeAsk(null);
            }}
          >
            {t('timer.progress.notYet')}
          </button>
        </div>
      )}
    </div>
  );
}
